import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Menu, X, Moon, Sun } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ isDarkMode, onToggleDarkMode }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', onClick: () => navigate('/') },
    { label: 'Explore', href: '/explore', onClick: () => navigate('/explore') },
    { label: 'Bookmarks', href: '/bookmarks', onClick: () => navigate('/bookmarks') },
    { label: 'About', href: '/about', onClick: () => navigate('/about') },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-200 ${
        isScrolled
          ? 'bg-cream dark:bg-velvet shadow-lg'
          : 'bg-cream/80 dark:bg-velvet/80 backdrop-blur-sm'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-sapphire to-gold rounded-lg flex items-center justify-center shadow-lg">
              <BookOpen className="h-6 w-6 text-cream" />
            </div>
            <span className="hidden sm:block font-serif font-bold text-sapphire dark:text-cream-light text-xl">
              EduLearn
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  if (link.onClick) {
                    e.preventDefault();
                    link.onClick();
                  }
                }}
                className="text-sapphire dark:text-cream-light hover:text-gold dark:hover:text-gold transition-colors duration-300 font-medium text-sm"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Dark Mode Toggle */}
            <button
              onClick={onToggleDarkMode}
              className="p-2 rounded-lg transition-all duration-300 hover:bg-silver dark:hover:bg-sapphire-light"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-gold" />
              ) : (
                <Moon className="h-5 w-5 text-sapphire" />
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-silver dark:hover:bg-sapphire-light transition-colors"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5 text-sapphire dark:text-cream-light" />
              ) : (
                <Menu className="h-5 w-5 text-sapphire dark:text-cream-light" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-silver dark:border-sapphire-light animate-slideInDown">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="block py-2 text-sapphire dark:text-cream-light hover:text-gold transition-colors font-medium"
                onClick={() => {
                  setIsMenuOpen(false);
                  if (link.onClick) link.onClick();
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
